import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Sparkles, MessageCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { trackOnboardingEvent } from '../../lib/analytics/onboardingEvents';

/**
 * Pannello chat "Flusia AI" — vive dentro la SetupFloatingBubble in un tab.
 *
 * - Persiste la cronologia in sessionStorage (max 50 msg)
 * - POST a edge `onboarding-chat` con tutta la cronologia + Bearer JWT
 * - Fallback graceful: se LLM unavailable, mostra messaggio neutro nel flow
 * - Empty state con suggerimenti rapidi cliccabili
 *
 * Brand: chiaramente segnalato come "Flusia AI" (no impersonation umana).
 */

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** true se questo messaggio assistant e un errore (LLM unavailable) */
  isError?: boolean;
}

const STORAGE_KEY = 'flusia-chat-history';
const MAX_HISTORY = 50;

const SUGGESTED_PROMPTS = [
  'Come funziona la pipeline?',
  'Come collego WhatsApp?',
  'Come creo la prima automazione?',
  'Cos\'e un lead vs cliente?',
];

function readHistory(): ChatMessage[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage =>
        m && typeof m === 'object' && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string',
    );
  } catch {
    return [];
  }
}

function writeHistory(messages: ChatMessage[]): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY)));
  } catch {
    // ignore
  }
}

/**
 * Render minimo di "**testo**" come <strong>. Pattern semplice senza regex pesanti.
 */
function renderAssistantContent(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  const len = text.length;
  while (i < len) {
    const open = text.indexOf('**', i);
    if (open < 0) {
      parts.push(<span key={key++}>{text.slice(i)}</span>);
      break;
    }
    if (open > i) parts.push(<span key={key++}>{text.slice(i, open)}</span>);
    const close = text.indexOf('**', open + 2);
    if (close < 0) {
      parts.push(<span key={key++}>{text.slice(open)}</span>);
      break;
    }
    parts.push(
      <strong key={key++} className="font-semibold text-pink-700 dark:text-pink-300">
        {text.slice(open + 2, close)}
      </strong>,
    );
    i = close + 2;
  }
  return parts;
}

export function SetupChatPanel() {
  const [messages, setMessages] = useState<ChatMessage[]>(() => readHistory());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    writeHistory(messages);
    // Scroll a fondo a ogni nuovo messaggio
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const nextHistory: ChatMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextHistory);
    setInput('');
    setLoading(true);
    trackOnboardingEvent('flusia.onboarding.chat_message_sent', {});

    try {
      const { data, error } = await supabase.functions.invoke('onboarding-chat', {
        body: { messages: nextHistory },
      });

      if (error || !data) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Servizio momentaneamente non disponibile. Riprova tra poco o scrivici su WhatsApp.',
            isError: true,
          },
        ]);
        trackOnboardingEvent('flusia.onboarding.chat_error', { reason: 'invoke_failed' });
        return;
      }

      if (typeof data.content === 'string' && data.content.trim()) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.content.trim() }]);
        trackOnboardingEvent('flusia.onboarding.chat_response', {});
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: typeof data.error === 'string' && data.error === 'llm_unavailable'
              ? "Flusia AI non e raggiungibile in questo momento. Riprova tra poco."
              : 'Non ho capito, puoi riformulare?',
            isError: true,
          },
        ]);
        trackOnboardingEvent('flusia.onboarding.chat_error', { reason: typeof data.error === 'string' ? data.error : 'unknown' });
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Errore di rete. Controlla la connessione e riprova.',
          isError: true,
        },
      ]);
      trackOnboardingEvent('flusia.onboarding.chat_error', {
        reason: 'network',
        message: err instanceof Error ? err.message : 'unknown',
      });
    } finally {
      setLoading(false);
    }
  }, [messages, loading]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    void send(input);
  }, [input, send]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  }, [input, send]);

  const showSuggestions = useMemo(() => messages.length === 0 && !loading, [messages.length, loading]);

  return (
    <div className="flex flex-col" style={{ height: 360 }}>
      {/* Header brand */}
      <div className="flex items-center gap-2 px-3.5 py-2 border-b border-[#EBEBEA] dark:border-white/10">
        <span
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F97316]"
          aria-hidden="true"
        >
          <Sparkles className="w-3 h-3 text-white" strokeWidth={2.5} />
        </span>
        <div className="min-w-0">
          <div className="text-[12.5px] font-semibold text-[#37352F] dark:text-white leading-tight">
            Flusia AI
          </div>
          <div className="text-[10.5px] text-[#9B9A97] dark:text-white/40 leading-tight">
            Assistente onboarding · risposte istantanee
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2.5 space-y-2.5">
        {showSuggestions && (
          <div className="space-y-2">
            <p className="text-[12px] text-[#787774] dark:text-white/50 leading-snug">
              Chiedimi qualsiasi cosa sulla configurazione di Flusia. Esempi:
            </p>
            <div className="flex flex-col gap-1.5">
              {SUGGESTED_PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => void send(p)}
                  className="text-left text-[12px] px-2.5 py-1.5 rounded-md border border-[#EBEBEA] dark:border-white/10 text-[#37352F] dark:text-white/85 hover:border-pink-300 hover:bg-[#FDF2F8] dark:hover:bg-pink-900/10 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m, idx) => (
            <motion.div
              key={`msg-${idx}`}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-2.5 py-1.5 rounded-lg text-[12.5px] leading-snug whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-gradient-to-br from-[#E91E8C] to-[#F97316] text-white rounded-br-sm'
                    : m.isError
                      ? 'bg-red-50 dark:bg-red-900/15 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30 rounded-bl-sm'
                      : 'bg-[#F7F6F3] dark:bg-white/5 text-[#37352F] dark:text-white/90 rounded-bl-sm'
                }`}
              >
                {m.role === 'assistant' && !m.isError ? renderAssistantContent(m.content) : m.content}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {loading && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-lg bg-[#F7F6F3] dark:bg-white/5 inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F97316] animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F97316] animate-bounce" style={{ animationDelay: '120ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F97316] animate-bounce" style={{ animationDelay: '240ms' }} />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="px-2.5 py-2 border-t border-[#EBEBEA] dark:border-white/10 flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          maxLength={1500}
          placeholder="Chiedi a Flusia AI..."
          disabled={loading}
          className="flex-1 px-2.5 py-1.5 text-[12.5px] rounded-md border border-[#EBEBEA] dark:border-white/15 bg-white dark:bg-[#202022] text-[#37352F] dark:text-white placeholder:text-[#9B9A97] dark:placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-400 resize-none disabled:opacity-50"
          style={{ minHeight: 30, maxHeight: 80 }}
        />
        <button
          type="submit"
          disabled={!input.trim() || loading}
          className="shrink-0 inline-flex items-center justify-center w-8 h-[30px] rounded-md bg-gradient-to-br from-[#E91E8C] to-[#F97316] text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          aria-label="Invia"
        >
          <Send className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </form>
    </div>
  );
}

// Re-export icon for tab indicator
export const ChatTabIcon = MessageCircle;
